import '@toast-ui/editor-plugin-code-syntax-highlight/dist/toastui-editor-plugin-code-syntax-highlight.css';
import '@toast-ui/editor-plugin-color-syntax/dist/toastui-editor-plugin-color-syntax.css';
import 'prismjs/themes/prism.css';
import 'tui-color-picker/dist/tui-color-picker.css';
import './markdown.css';

import type { NewMediaUploadFn } from '../../hooks/api/useMediaUpload';
import type { WebsocketAwarenessState } from '../../hooks/useWebsocket/websocket';
import type { Media } from '../../lib/flotiq-client/types/api-response';
import type { HelpErrorTextsProps } from '../HelpErrorTextsTemplate/HelpErrorTextsTemplate';

import { useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import Editor from '@toast-ui/editor';
import codeSyntaxHighlightPlugin from '@toast-ui/editor-plugin-code-syntax-highlight';
import colorPlugin from '@toast-ui/editor-plugin-color-syntax';
import { twMerge } from 'tailwind-merge';

import { getEnv } from '../../lib/env';
import { getMediaUrl } from '../../lib/flotiq-client/api-helpers';
import { EMPTY_FUNCTION } from '../../lib/globals';
import { getTestProps } from '../../lib/helpers';

import { useModals } from '../../contexts/ModalContext';

import FieldEditorsIndicator from '../FieldEditorsIndicator/FieldEditorsIndicator';
import FileButton from '../FileButton/FileButton';
import HelpErrorTextsTemplate from '../HelpErrorTextsTemplate/HelpErrorTextsTemplate';
import LinkObjectContentModal from '../LinkObjectContentModal/LinkObjectContentModal';
import RequiredTemplate from '../RequiredTemplate/RequiredTemplate';

export interface MarkdownProps extends HelpErrorTextsProps {
  /**
   * Field form name
   */
  name?: string;
  /**
   * Markdown value
   */
  value?: string;
  /**
   * On change handler
   */
  onChange?: (value: string, name?: string) => void;
  /**
   * On blur handler
   */
  onBlur?: (name?: string) => void;
  /**
   * On focus handler
   */
  onFocus?: (name?: string) => void;
  /**
   * Label above the editor
   */
  label?: string;
  /**
   * If value is required
   */
  required?: boolean;
  /**
   * If the editor is disabled
   */
  disabled?: boolean;
  /**
   * If editor is readonly (non-interactive but without disabled classes)
   */
  readonly?: boolean;
  /**
   * On media upload callback
   */
  onMediaUpload?: NewMediaUploadFn;
  /**
   * Editors currently editing the field
   */
  editors?: WebsocketAwarenessState[];
  /**
   * Additional classes for editor
   */
  additionalClasses?: string;
  /**
   * Markdown test id
   */
  testId?: string;
}

const Markdown = ({
  name,
  value = '',
  onChange = EMPTY_FUNCTION,
  onBlur = EMPTY_FUNCTION,
  onFocus,
  label = '',
  error,
  required = false,
  disabled = false,
  readonly = false,
  helpText = '',
  onMediaUpload,
  editors,
  additionalClasses = '',
  testId = '',
}: MarkdownProps) => {
  const editor = useRef<typeof Editor>(null);
  const editorElement = useRef<HTMLDivElement>(null);
  const modal = useModals();
  const { t } = useTranslation();

  const handleDisabled = useCallback(() => {
    if (!editor.current) return;

    const editableElements = [
      ...editor.current
        .getEditorElements()
        .mdEditor.querySelectorAll('div[contentEditable]'),
      ...editor.current
        .getEditorElements()
        .wwEditor.querySelectorAll('div[contentEditable]'),
    ] as HTMLDivElement[];

    if (disabled || readonly) {
      editableElements.forEach((el) => (el.contentEditable = 'false'));
    } else {
      editableElements.forEach((el) => (el.contentEditable = 'true'));
    }
  }, [disabled, readonly]);

  useEffect(() => {
    handleDisabled();
  }, [handleDisabled]);

  useEffect(() => {
    if (!editor.current) return;
    const currentValue = editor.current.getMarkdown();
    if (value !== currentValue) {
      editor.current.setMarkdown(value);
    }
  }, [name, value]);

  const handleChange = useCallback(() => {
    if (!editor.current) return;
    const currentValue = editor.current.getMarkdown();
    onChange(currentValue, name);
  }, [onChange, name]);

  const addMediaToEditor = useCallback(async () => {
    const media: Record<string, Media> = await modal({
      title: (
        <div className="flex flex-wrap justify-between gap-1">
          <div className="text-3xl">{t('Global.MediaLibrary')}</div>
          <FileButton onUpload={onMediaUpload} multiple testId={testId} />
        </div>
      ),
      content: (
        <LinkObjectContentModal
          relationType="_media"
          onMediaUpload={onMediaUpload}
          returnContentObject
          isMultiple={
            getEnv('VITE_TEXT_EDITORS_MULTIPLE_MEDIA_MODAL') === 'true'
          }
          {...getTestProps(testId, 'link-modal', 'testId')}
        />
      ),
      size: '2xl',
      dialogAdditionalClasses: 'h-[calc(100vh-32px)]',
    });
    if (media && Object.keys(media).length) {
      Object.values(media).forEach((newMedia) => {
        const mediaUrl = getMediaUrl(newMedia);
        if (newMedia.type === 'image') {
          editor.current?.exec?.('addImage', {
            altText: newMedia.fileName,
            imageUrl: mediaUrl,
          });
        } else {
          editor.current?.exec?.('addLink', {
            linkText: newMedia.fileName,
            linkUrl: mediaUrl,
          });
        }
      });
    }
  }, [onMediaUpload, modal, t, testId]);

  const handleFullScreen = useCallback(() => {
    if (!editorElement.current) return false;
    editorElement.current.classList.toggle('full-size-editor');
    return true;
  }, [editorElement]);

  const handleEditorLoad = useCallback(() => {
    if (!editorElement.current || !editor.current) return;

    handleDisabled();

    editor.current.addCommand('markdown', 'fullSize', () => handleFullScreen());
    editor.current.addCommand('wysiwyg', 'fullSize', () => handleFullScreen());

    editor.current.addCommand('markdown', 'addMediaToEditor', () => {
      addMediaToEditor();
      return true;
    });
    editor.current.addCommand('wysiwyg', 'addMediaToEditor', () => {
      addMediaToEditor();
      return true;
    });
  }, [addMediaToEditor, handleDisabled, handleFullScreen]);

  useEffect(() => {
    if (editor.current || !editorElement.current) return;

    editor.current = new Editor({
      el: editorElement.current,
      height: '400px',
      usageStatistics: false,
      initialValue: value,
      events: {
        change: handleChange,
        blur: () => onBlur(name),
        focus: () => onFocus?.(name),
      },
      plugins: [codeSyntaxHighlightPlugin, colorPlugin],
      initialEditType: 'markdown',
      previewStyle: 'tab',
      autofocus: false,
      toolbarItems: [
        [
          {
            className: 'toastui-editor-stretch-button',
            name: 'full-screen',
            tooltip: 'Full screen',
            command: 'fullSize',
          },
        ],
        ['heading', 'bold', 'italic', 'strike'],
        ['hr', 'quote'],
        ['ul', 'ol', 'task', 'indent', 'outdent'],
        [
          'table',
          'image',
          {
            className: 'toastui-editor-media-button',
            name: 'add-media-from-library',
            tooltip: 'Select media from media library',
            command: 'addMediaToEditor',
          },
          'link',
        ],
        ['code', 'codeblock'],
      ],
    });

    handleEditorLoad();
  }, [
    editorElement,
    value,
    disabled,
    handleChange,
    onBlur,
    handleDisabled,
    handleFullScreen,
    addMediaToEditor,
    handleEditorLoad,
    name,
    onFocus,
  ]);

  useEffect(() => {
    return () => {
      if (!editor.current) return;
      if (editor.current.destroy) {
        editor.current.destroy();
        editor.current = null;
      }
    };
  }, []);

  return (
    <div
      className={twMerge(
        'w-full',
        'flex',
        'flex-col',
        'relative',
        additionalClasses,
      )}
    >
      <label
        className="text-sm text-slate-400 dark:text-slate-200 mb-1"
        {...getTestProps(testId, 'label')}
      >
        {label}
        {required && <RequiredTemplate />}
      </label>

      <FieldEditorsIndicator editors={editors}>
        <div
          data-field-name={name}
          ref={editorElement}
          className={twMerge(
            'border rounded-lg',
            error ? 'border-red' : 'border-slate-200',
            disabled ? 'bg-slate-100 disabled-editor' : 'bg-white',
          )}
          {...getTestProps(testId, 'editor')}
        />
      </FieldEditorsIndicator>

      <HelpErrorTextsTemplate helpText={helpText} error={error} />
    </div>
  );
};

export default Markdown;
